import type { Design_ } from 'src/api/db/apiDesigns'

export type PlacementMode = 'manual' | 'auto'

export type GenerateMockupsDesign = {
  x?: number
  y?: number
  w?: number
  h?: number
  warp_transform?: string | null
  brightness?: number | null
  saturation?: number | null
  hue?: number | null
  colorFilter?: string | null
  rotation_z?: number | null
}

export type BuildManualMockupPayloadInput = {
  quote_sub_item_id: number
  quote_item_id: number
  product_name: string
  decoration_name: string | null
  design: Partial<Design_>
}

export type BuildAutoMockupPayloadInput = {
  quote_sub_item_id: number
  quote_item_id: number
  product_name: string
  decoration_name: string | null
  product_image_url: string | null
  logo_url: string | null
  product_color?: string | null
  custom_prompt?: string | null
  design?: Partial<Design_> | null
}

export const buildManualMockupPayload = ({
  quote_sub_item_id,
  quote_item_id,
  product_name,
  decoration_name,
  design,
}: BuildManualMockupPayloadInput) => {
  if (!design?.image_url || !design?.design_url) {
    return {
      payload: null,
      reason: 'Select a product image and logo to generate mockups.',
    }
  }

  return {
    payload: {
      quote_sub_item_id,
      quote_item_id,
      product_name,
      decoration_name,
      product_image_url: design.image_url,
      logo_url: design.design_url,
      placement_mode: 'manual' as PlacementMode,
      design: {
        x: design.x ?? undefined,
        y: design.y ?? undefined,
        w: design.w ?? undefined,
        h: design.h ?? undefined,
        warp_transform: design.warp_transform,
        brightness: design.brightness ?? undefined,
        saturation: design.saturation ?? undefined,
        hue: design.hue ?? undefined,
        colorFilter: design.colorFilter ?? undefined,
        rotation_z: design.rotation_z ?? undefined,
      },
    },
    reason: null,
  }
}

export const buildAutoMockupPayload = ({
  quote_sub_item_id,
  quote_item_id,
  product_name,
  decoration_name,
  product_image_url,
  logo_url,
  product_color,
  custom_prompt,
  design,
}: BuildAutoMockupPayloadInput) => {
  if (!product_image_url) {
    return { payload: null, reason: 'No product image available.' }
  }
  if (!logo_url) {
    return { payload: null, reason: 'Choose a logo to generate a mockup.' }
  }

  const designCoords = design
    ? {
        x: design.x ?? undefined,
        y: design.y ?? undefined,
        w: design.w ?? undefined,
        h: design.h ?? undefined,
        warp_transform: design.warp_transform,
        rotation_z: design.rotation_z ?? undefined,
        brightness: design.brightness ?? undefined,
        saturation: design.saturation ?? undefined,
        hue: design.hue ?? undefined,
        colorFilter: design.colorFilter ?? undefined,
      }
    : undefined

  return {
    payload: {
      quote_sub_item_id,
      quote_item_id,
      product_name,
      decoration_name,
      product_image_url,
      logo_url,
      product_color: product_color?.trim() || null,
      placement_mode: 'auto' as PlacementMode,
      custom_prompt: custom_prompt?.trim() || null,
      design: designCoords,
    },
    reason: null,
  }
}
